export default {
    /** Shared by the Forms card (website settings) and the Form settings dialog. */
    shared: {
        fromEmail: 'Sender email (optional)',
        fromEmailHint: "Override the website's default mail-from for submissions of this form.",
        fromName: 'Sender name (optional)',
        fallsBack: 'Falls back to the website default',
        recipientPlaceholder: "info{'@'}example.com",
        successUrlPlaceholder: '/thank-you',
    },
    /** Forms card on the website edit screen. */
    card: {
        title: 'Forms',
        intro: "Forms are discovered automatically from the active theme's templates ({code}). Configure each one with where submissions should go.",
        empty: 'No forms detected in the active theme yet.',
        removeConfirm: 'Remove config?',
        removeTitle: 'Remove configuration (keeps the form in the theme; submissions will 404)',
        recipient: 'Recipient email',
        subject: 'Email subject',
        subjectPlaceholder: 'Contact form',
        subjectHint: "Subject line for the notification email. {'{'}field:name{'}'} adds what was filled in for that field (its name in the form), e.g. Quote request {'{'}field:service{'}'}.",
        successUrl: 'Redirect after success',
        successUrlHint: 'Leave blank to redirect back to the page that hosted the form.',
        configure: 'Configure',
        status: {
            active: 'active',
            detected: 'detected, not configured',
            orphan: 'orphan (not in theme)',
            unknown: 'unknown',
        },
    },
    /** Form settings dialog, opened from a form's cog in the page editor. */
    settings: {
        title: 'Form settings',
        delivery: 'Delivery',
        recipients: 'Recipient(s)',
        recipientsHint: 'Email address that receives submissions. Comma-separate for multiple.',
        subject: 'Subject',
        subjectHint: "Subject line for the notification email. {'{'}field:name{'}'} adds what was filled in for that field (its name in the form), e.g. Quote request {'{'}field:service{'}'}.",
        subjectPlaceholder: 'Contact form submission',
        successUrl: 'Success URL',
        successUrlHint: 'Path to redirect to after a successful submission. Leave blank to show the inline success message.',
        retention: 'Keep uploaded files for (days)',
        retentionHint: 'Applies only to forms that accept file uploads. After this the files are deleted; the submission is kept and shows the attachment as expired.',
        retentionPlaceholder: '{days} (default)',
        labelsAndText: 'Labels & text',
        defaultLocale: 'default',
        fieldLabels: 'Field labels',
        placeholders: 'Placeholders',
        button: 'Button',
        submitButton: 'Submit button',
    },
    /** Field settings dialog: the per-field editor behind a page field's cog. */
    field: {
        title: 'Edit · {name}',
        titleFallback: 'Edit field',
        pickImage: 'Pick or upload an image',
        pickVideo: 'Pick or upload a video',
        pickAny: 'Pick or upload an image or video',
        noImage: 'No image',
        noVideo: 'No video',
        source: 'Source',
        replace: 'Replace…',
        choose: 'Choose…',
        altText: 'Alt text',
        alt: 'Alt',
        caption: 'Caption',
        width: 'Width',
        alignment: 'Alignment',
        cornerRadius: 'Corner radius',
        fit: 'Fit',
        size: {
            small: 'Small',
            medium: 'Medium',
            large: 'Large',
            full: 'Full',
        },
        align: {
            left: 'Left',
            center: 'Center',
            right: 'Right',
        },
        radius: {
            circle: 'Circle',
            same: 'Same',
        },
        fitOption: {
            auto: 'Auto',
            cover: 'Cover',
        },
        translate: {
            editing: 'Editing {locale}.',
            editingFrom: 'Editing {locale}, translated from {source}.',
            button: 'Translate this field',
            buttonTitle: 'Fill the box below with a {locale} translation. Nothing is saved until you press Apply and then Save.',
            done: 'Translated. Review it, then press Apply',
            skipped: 'The server left this field alone: nothing was changed',
            failed: 'The translator could not return this field',
        },
        html: {
            content: 'Content',
        },
        text: {
            label: 'Text',
            hint: 'The theme decides the surrounding tag (heading, paragraph, etc.). Inline marks only.',
        },
        image: {
            altHint: 'Describe the image for screen readers and SEO.',
            focalPoint: 'Focal point',
            resetFocal: 'Reset to centre',
            focalHelp: 'Click or drag to choose what stays in view when the image is cropped to fit.',
        },
        gallery: {
            columns: 'Columns',
            columnsAuto: 'Auto',
            items: 'Items',
            add: 'Add image or video',
            empty: 'No images yet. Click “Add image or video” to pick from the library.',
        },
        slideshow: {
            translatingNotice: 'Editing {locale} text only. The images, button targets and timing are shared with every language. Change those on the {source} tab and they follow here automatically.',
            height: 'Height',
            heightSmall: 'Small (40vh)',
            heightMedium: 'Medium (60vh)',
            heightLarge: 'Large (80vh)',
            heightFull: 'Full (100vh)',
            heightFromTheme: 'This theme sets the slider height itself, so this setting has no effect on this site.',
            fit: 'Photo fit',
            fitCover: 'Fill the slider',
            fitContain: 'Whole photo',
            fitCoverHint: 'Fills the slider; edges that do not fit are cut off.',
            fitContainHint: 'Shows all of every photo; the space around it gets a soft copy of the photo.',
            interval: 'Interval (ms)',
            intervalHint: 'Time between slides, between 1000 and 60000.',
            pauseOnHover: 'Pause on hover',
            showDots: 'Show dots',
            showArrows: 'Show arrows',
            slides: 'Slides',
            add: 'Add slide',
            heading: 'Heading',
            altHint: 'Describes the image (required for accessibility).',
            cta: 'Call-to-action',
            ctaNone: 'No button',
            ctaUrl: 'External URL',
            ctaPage: 'Page on this site',
            ctaCategory: 'Webshop category',
            ctaProduct: 'Product',
            buttonLabel: 'Button label',
            buttonLabelHint: 'Shown in {locale}.',
            urlPlaceholder: 'https://example.com or /contact',
            urlHint: 'Absolute http(s) URL or root-relative path.',
            page: 'Page',
            pageHint: 'Pick a page on this website. Renamed pages need to be re-picked here.',
            category: 'Category',
            categoryHint: 'Top-level webshop categories. Each language links to its own translated address.',
            product: 'Product',
            productHint: 'Type to search the catalogue. Each language links to its own translated address.',
            empty: 'No slides yet. Click “Add slide” to pick from the library.',
        },
        video: {
            autoplay: 'Play automatically',
            autoplayHint: 'Always silent: browsers refuse to autoplay a video with sound. Best for a short background loop.',
            loop: 'Loop',
            controls: 'Show player controls',
            controlsHint: "Turn off for a background video the visitor shouldn't pause.",
            mobile: 'On phones',
            mobileHint: 'A still image saves a large download on the smallest screen and the worst connection.',
            mobileVideo: 'Play the video',
            mobilePoster: 'Show a still image',
            altHint: 'Describes the video for screen readers, and labels the still image.',
            hideCorners: 'Hide',
            setCorners: 'Set corners individually',
            cornerTopLeft: 'Top left',
            cornerTopRight: 'Top right',
            cornerBottomLeft: 'Bottom left',
            cornerBottomRight: 'Bottom right',
            expandable: 'Let visitors enlarge it',
            expandableHint: 'Opens bigger on the page, not in browser fullscreen. Clicking beside it puts it back.',
        },
        compare: {
            before: 'Before',
            after: 'After',
            missingBoth: 'Pick both images. A comparison needs two.',
            missingAfter: 'Pick the "after" image too.',
            missingBefore: 'Pick the "before" image too.',
            imageLabel: 'Label on the image',
            imageLabelHint: 'Optional.',
            startingPosition: 'Starting position',
            startingHelp: '{n}% of the "after" image is showing before anyone drags.',
            direction: 'Slide direction',
            horizontal: 'Left and right',
            vertical: 'Top and bottom',
        },
        embed: {
            code: 'HTML / iframe code',
            hint: 'Sanitised on save: only safe iframe and script tags from approved hosts survive.',
        },
        divider: {
            none: "Divider fields have no settings: they're a visual separator only.",
        },
        unknown: 'No editor available for {type} fields yet.',
    },
};
